import { useState, useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, useNavigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import axios from 'axios';
import LandingPage from './pages/LandingPage';
import Dashboard from './pages/Dashboard';
import Blog from './pages/Blog';
import BlogPage from './pages/BlogPage';
import ContentStrategy from './pages/ContentStrategy';
import Profile from './pages/Profile';
import ContentCalendar from './pages/ContentCalendar';
import ContentLibrary from './pages/ContentLibrary';
import Integrations from './pages/Integrations';
import PaymentSuccess from './pages/PaymentSuccess';
import AuthModal from './components/AuthModal';
import AdminDashboard from './pages/AdminDashboard';
import IndustryMartialArts from './pages/IndustryMartialArts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export { API };

// Progress Banner Component
const GenerationProgress = ({ user }) => {
  const [activeJob, setActiveJob] = useState(null);
  
  useEffect(() => {
    if (!user) return;
    
    const checkJob = async () => {
      try {
        const response = await axios.get(`${API}/usage/${user.id}`);
        if (response.data.active_job) {
          setActiveJob(response.data.active_job);
        } else {
          setActiveJob(null);
        }
      } catch (error) {
        console.error('Error checking job status:', error);
      }
    };
    
    // Check immediately
    checkJob();
    
    // Poll every 5 seconds
    const interval = setInterval(checkJob, 5000);
    return () => clearInterval(interval);
  }, [user]);
  
  if (!activeJob) return null;
  
  return (
    <div className="fixed bottom-4 right-4 bg-orange-500 text-white px-4 py-3 rounded-lg shadow-lg z-50 max-w-sm">
      <div className="flex items-center gap-3">
        <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
        <div>
          <p className="font-medium">Generating blogs...</p>
          <p className="text-sm text-orange-100">
            {activeJob.completed_count} of {activeJob.total_count} complete
          </p>
        </div>
      </div>
    </div>
  );
};

function App() {
  const [user, setUser] = useState(null);
  const [showAuth, setShowAuth] = useState(false);

  useEffect(() => {
    // Check for saved token
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
    }
  }, []);

  const handleLogin = (userData, token) => {
    setUser(userData);
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setShowAuth(false);
    // Redirect to dashboard after login
    window.location.href = '/dashboard';
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  };

  return (
    <HelmetProvider>
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage user={user} onLogin={() => setShowAuth(true)} onLogout={handleLogout} />} />
          <Route path="/dashboard" element={<Dashboard user={user} onLogout={handleLogout} />} />
          <Route path="/profile" element={<Profile user={user} />} />
          <Route path="/keyword-research" element={<ContentStrategy user={user} />} />
          <Route path="/content-strategy" element={<ContentStrategy user={user} />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/content-calendar" element={<ContentCalendar user={user} />} />
          <Route path="/content-library" element={<ContentLibrary user={user} />} />
          <Route path="/integrations" element={<Integrations user={user} />} />
          <Route path="/payment-success" element={<PaymentSuccess user={user} />} />
          <Route path="/admin" element={<AdminDashboard user={user} />} />
          <Route path="/industries/martial-arts" element={<IndustryMartialArts user={user} onLogin={() => setShowAuth(true)} />} />
        </Routes>
        
        {/* Global generation progress indicator */}
        <GenerationProgress user={user} />
      </BrowserRouter>
      
      {showAuth && <AuthModal onClose={() => setShowAuth(false)} onLogin={handleLogin} />}
    </div>
    </HelmetProvider>
  );
}

export default App;